import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOSchema from '@/components/feature/SEOSchema';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.slug === slug);
  const relatedArticles = articles.filter(a => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 pb-10 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/articles" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回文章列表</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishedAt,
        image: article.imageUrl,
        inLanguage: 'zh-CN',
        publisher: {
          '@type': 'Organization',
          name: '久久精品',
        },
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiuboutique.com/' },
          { '@type': 'ListItem', position: 2, name: '精选文章', item: 'https://jiujiuboutique.com/articles' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://jiujiuboutique.com/article/${article.slug}` },
        ],
      }} />

      <Navbar />
      <main className="min-h-screen pt-20">
        <Breadcrumb items={[
          { label: '精选文章', path: '/articles' },
          { label: article.title },
        ]} />

        <article className="w-full py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4">
                <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">{article.category}</span>
                <span>{article.author}</span>
                <span>{article.publishedAt}</span>
                <span>{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="relative overflow-hidden rounded-lg aspect-[2/1] mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} | 久久精品`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900
                [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-heading [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base
                [&_strong]:text-foreground-900
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}